import React, {useEffect, useState} from 'react';
import {Button, TextField, Typography} from '@mui/material';
import PriceSlider from '../sliders/PriceSlider';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../store';

export type PriceFilterProps = {
  min: number;
  max: number;
  onChange: (min: number, max: number) => void;
};

const PriceFilter: React.FC<PriceFilterProps> = ({min, max, onChange}) => {
  const currencyRate = useSelector((state: RootState) => state.currency.rate);
  const currencySymbol = useSelector((state: RootState) => state.currency.symbol);
  const currencyName = useSelector((state: RootState) => state.currency.currency);

  const BASE_MAX = 100000;
  const dynamicMaxLimit = BASE_MAX * currencyRate;

  const [open, setOpen] = useState(true);

  // Local state for the string inputs (what the user sees)
  const [minInput, setMinInput] = useState((min * currencyRate).toFixed(2));
  const [maxInput, setMaxInput] = useState((max * currencyRate).toFixed(2));

  // Local numeric state for the slider (immediate visual feedback)
  const [localMin, setLocalMin] = useState(min * currencyRate);
  const [localMax, setLocalMax] = useState(max * currencyRate);

  // Sync with props ONLY when external values (like currency or a reset) change significantly
  useEffect(() => {
    const calculatedMin = min * currencyRate;
    const calculatedMax = max * currencyRate;

    // Update only if the difference is more than a small margin to avoid jumping while typing
    if (Math.abs(localMin - calculatedMin) > 0.01 || Math.abs(localMax - calculatedMax) > 0.01) {
      setLocalMin(calculatedMin);
      setLocalMax(calculatedMax);
      setMinInput(calculatedMin.toFixed(2));
      setMaxInput(calculatedMax.toFixed(2));
    }
  }, [min, max, currencyRate]);

  // Trigger API while typing with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      onChange(localMin / currencyRate, localMax / currencyRate);
    }, 300);

    return () => clearTimeout(timer);
  }, [localMin, localMax, currencyRate, onChange]);

  const handleInputChange = (value: string, type: 'min' | 'max') => {
    // Allow digits and one dot/comma
    const sanitized = value.replace(/,/g, '.').replace(/[^0-9.]/g, '');

    if (type === 'min') {
      setMinInput(sanitized);
      const num = parseFloat(sanitized);
      if (!isNaN(num)) setLocalMin(num); // Update slider live
    } else {
      setMaxInput(sanitized);
      const num = parseFloat(sanitized);
      if (!isNaN(num)) setLocalMax(num); // Update slider live
    }
  };

  const handleQuickFilter = (targetMinBase: number, targetMaxBase: number) => {
    const finalMin = targetMinBase * currencyRate;
    const finalMax = targetMaxBase * currencyRate;

    setLocalMin(finalMin);
    setLocalMax(finalMax);
    setMinInput(finalMin.toFixed(2));
    setMaxInput(finalMax.toFixed(2));

    onChange(targetMinBase, targetMaxBase);
  };

  const formatLabel = (val: number) => Math.round(val * currencyRate).toLocaleString();

  const textFieldSx = {
    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
      display: 'none',
      margin: 0,
    },
    '& input[type=number]': {
      MozAppearance: 'textfield',
    },
    '& .MuiInputBase-input': {
      fontSize: '0.80rem',
      fontWeight: 600,
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.80rem',
      fontWeight: 600,
    },
  };

  return (
      <div className="border border-blue-700/60 rounded-md overflow-hidden">
        <button
            onClick={() => setOpen(!open)}
            className={`w-full flex justify-between items-center px-3 py-2 text-gray-300 ${
                open ? 'bg-blue-900/40' : ''
            }`}
        >
          <span className="!text-[0.80rem] !font-semibold">Preis</span>
          <span className="!text-[0.80rem] !font-semibold">{open ? "▲" : "▼"}</span>
        </button>

        <div
            className={`transition-all duration-300 overflow-hidden ${
                open ? 'max-h-[9999px]' : 'max-h-0'
            }`}
        >
          <div className="px-3 pb-4 pt-2 space-y-3">
            <Typography className="text-gray-300 !text-[0.80rem] !font-semibold">
              In {currencyName}
            </Typography>

            <PriceSlider
                value={[localMin, localMax]}
                min={0}
                max={dynamicMaxLimit}
                onChange={(a, b) => {
                  setLocalMin(a);
                  setLocalMax(b);
                  setMinInput(a.toFixed(2));
                  setMaxInput(b.toFixed(2));
                }}
                onCommit={() => onChange(localMin / currencyRate, localMax / currencyRate)}
            />
            <div className="flex gap-2">
              <TextField
                  size="small"
                  label="Min"
                  value={minInput}
                  onChange={(e) => handleInputChange(e.target.value, 'min')}
                  onBlur={() => setMinInput(localMin.toFixed(2))}
                  className="flex-1"
                  sx={textFieldSx}
              />

              <TextField
                  size="small"
                  label="Max"
                  value={maxInput}
                  onChange={(e) => handleInputChange(e.target.value, 'max')}
                  onBlur={() => setMaxInput(localMax.toFixed(2))}
                  className="flex-1"
                  sx={textFieldSx}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button className="!text-[0.80rem] !font-semibold" size="small" variant="outlined" onClick={() => handleQuickFilter(0, 20)}>
                &lt; {currencySymbol}{formatLabel(20)}
              </Button>
              <Button className="!text-[0.80rem] !font-semibold" size="small" variant="outlined" onClick={() => handleQuickFilter(20, 100)}>
                {currencySymbol}{formatLabel(20)} – {currencySymbol}{formatLabel(100)}
              </Button>
              <Button className="!text-[0.80rem] !font-semibold" size="small" variant="outlined" onClick={() => handleQuickFilter(100, 100000)}>
                &gt; {currencySymbol}{formatLabel(100)}
              </Button>
            </div>
          </div>
        </div>
      </div>
  );
};

export default PriceFilter;